import { Box } from "@mui/joy";
import React, { useEffect, useRef, useState } from "react";
import StringInputAutoForm from "./form-filling-auto/StringInput";
import SelectInputAutoForm from "./form-filling-auto/SelectInput";
import DateInput from "./form-filling-auto/DateInput";
import TimeInput from "./form-filling-auto/TimeInput";
import RadioButtonAutoForm from "./form-filling-auto/RadioButton";
import TextAreaInput from "./form-filling-auto/TextAreaInput";
import VitalsTableInput from "./form-filling-auto/VitalsTableInput";
import IntegerInputAutoForm from "./form-filling-auto/IntegerInput";
import HeadersTableInput from "./form-filling-auto/HeadersTableInput";
import { LocationOfInjuries } from "./form-filling-auto/LocationOfInjuries";
import { convertJSONKeyToTitle } from "../helpers/KeyConverters";

import CheckBoxAutoForm from "./form-filling-auto/CheckBoxButton";


function AutoBuildFormSection(props) {
    const formSectionInputs = useRef({});
    const [ maxLabelWidth, setMaxLabelWidth ] = useState(0);
    const [ready, setReady] = useState(false);

    const setupComponents = () => {
        try {
            const newSectionInputs = {}
            for(const key in props.formFields) {
                if (!props.initialInput || !(key in props.initialInput)) {
                    newSectionInputs[key] = undefined;
                } else {
                    newSectionInputs[key] = props.initialInput[key]
                }
            }
            formSectionInputs.current = newSectionInputs;
        } catch(error) {
            console.log(error);
        }
    };
    
    const calculateMaxLabelWidth = (formFields) => {
        const labelWidths = Object.keys(formFields).map(key => 
            convertJSONKeyToTitle(key).length
        );
        
        // Sort the widths in descending order
        labelWidths.sort((a, b) => b - a);
        
        // Normally, 20 chars isn't exceeded for the labels
        const maxAllowedWidth = 20;
        
        // If the highest width exceeds the maximum allowed, use the second highest
        if (labelWidths[0] > maxAllowedWidth && labelWidths.length > 1) {
            return labelWidths[1];
        }
        
        return labelWidths[0];
    };

    useEffect(() => {
        // Use props.formFields, iterate through each key value pair to determine the amount of values needed 
        const setup = () => {
            setupComponents();
            setMaxLabelWidth(calculateMaxLabelWidth(props.formFields));
            setReady(true);
        }

        setup()
    }, [props.formFields]);

    return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, pb: "2rem"}}>
            {/* Enumerate the formAutoState, depending on the parsed type, render the proper form fields => Use map, then if statement checking the form type*/}
                {ready && props.formFields["field_order"].map((key) => {
                    if (props.formFields[key]["type"] === 'string') {
                        if (props.formFields[key]["display"] === "dropdown") {
                            return (
                                <Box sx={{ flex: '1 1 45%', pl: '1rem' }}> 
                                    <SelectInputAutoForm 
                                        key={key}
                                        name={`${props.section}[${key}]`}
                                        label={convertJSONKeyToTitle(key)}
                                        multiple={false}
                                        disabled={props.disabled}
                                        labelMinWidth={`${maxLabelWidth * 8}px`} // Approximate character width
                                        options={props.formFields[key].values}
                                        defaultValue={formSectionInputs.current[key]}
                                        required={props.formFields[key].required}
                                    />
                                </Box>
                            );
                        } 
                        else if (props.formFields[key]["display"] === "textarea") {
                            if (props.formFields[key]["size"] === "medium") {
                                return (
                                    <Box sx={{ flex: '45%', px: '1%'}}>
                                        <TextAreaInput
                                            key={key}
                                            name={`${props.section}[${key}]`}
                                            disabled={props.disabled}
                                            label={convertJSONKeyToTitle(key)}
                                            labelMinWidth={`${maxLabelWidth * 8}px`} // Approximate character width
                                            defaultValue={formSectionInputs.current[key]}
                                            required={props.formFields[key].required}
                                        />
                                    </Box>
                                );
                            } else if (props.formFields[key]["size"] === "small") {
                                return (
                                    <Box sx={{ flex: '30%', px: '1%', height: "9%"}}>
                                        <TextAreaInput
                                            key={key}
                                            name={`${props.section}[${key}]`}
                                            height={"100%"}
                                            disabled={props.disabled}
                                            label={convertJSONKeyToTitle(key)}
                                            labelMinWidth={`${maxLabelWidth * 8}px`} // Approximate character width
                                            defaultValue={formSectionInputs.current[key]}
                                            required={props.formFields[key].required}
                                        />
                                    </Box>
                                );
                            } else {
                                return (
                                    <Box sx={{ flex: '100%', pl: '1rem' }}>
                                        <TextAreaInput
                                            key={key}
                                            name={`${props.section}[${key}]`}
                                            label={convertJSONKeyToTitle(key)}
                                            disabled={props.disabled}
                                            labelMinWidth={`${maxLabelWidth * 8}px`} // Approximate character width
                                            defaultValue={formSectionInputs.current[key]}
                                            required={props.formFields[key].required}
                                        />
                                    </Box>
                                );
                            }
                        }
                        else {
                            return (
                                <Box sx={{ 
                                    flex: (() => {
                                        // If in past visits
                                        if (props.disabled && typeof formSectionInputs.current[key] === 'string') {
                                            return formSectionInputs.current[key].length > (formSectionInputs.current[key] * 1.44) ? '1 1 100%' : '1 1 45%';
                                        }
                                        else {
                                            return '1 1 45%'
                                        }
                                    })(),
                                    pl: '1rem'
                                }}>
                                    <StringInputAutoForm 
                                        key={key}
                                        name={`${props.section}[${key}]`}
                                        label={convertJSONKeyToTitle(key)} 
                                        disabled={props.disabled}
                                        labelMinWidth={`${maxLabelWidth * 8}px`} // Approximate character width
                                        defaultValue={formSectionInputs.current[key]}
                                        required={props.formFields[key].required}
                                    />
                                </Box>
                            )
                        }
                    }
                    else if (props.formFields[key]["type"] === 'integer') {
                        return (
                            <Box sx={{ flex: '1 1 45%', pl: '1rem' }}>
                                <IntegerInputAutoForm
                                    key={key}
                                    name={`${props.section}[${key}]`}
                                    label={convertJSONKeyToTitle(key)}
                                    disabled={props.disabled}
                                    labelMinWidth={`${maxLabelWidth * 8}px`}
                                    defaultValue={formSectionInputs.current[key]}
                                    required={props.formFields[key].required}
                                />
                            </Box>
                        )
                    }
                    else if (props.formFields[key]["type"] === 'datetime') {
                        if (props.formFields[key]["display"] === "datetime_date") {
                            return (
                                <Box sx={{ flex: '1 1 45%', pl: '1rem' }}>
                                    <DateInput
                                        key={key}
                                        name={`${props.section}[${key}]`}
                                        label={convertJSONKeyToTitle(key)} 
                                        disabled={props.disabled}
                                        labelMinWidth={`${maxLabelWidth * 8}px`} // Approximate character width
                                        defaultValue={formSectionInputs.current[key]}
                                        required={props.formFields[key].required}
                                    />
                                </Box>
                            );
                        } else if (props.formFields[key]["display"] === "datetime_hour") {
                            return (
                                <Box sx={{ flex: '1 1 45%', pl: '1rem' }}>
                                    <TimeInput
                                        key={key}
                                        label={key === "efrt_activation_time" ? "EMS Activation Time" : convertJSONKeyToTitle(key)} 
                                        name={`${props.section}[${key}]`}
                                        disabled={props.disabled}
                                        labelMinWidth={`${maxLabelWidth * 8}px`} // Approximate character width
                                        defaultValue={formSectionInputs.current[key]}
                                        required={props.formFields[key].required}
                                    />
                                </Box>
                            );
                        }
                    }
                    else if (props.formFields[key]["type"] === 'toggle') {
                        return (
                            <Box sx={{ flex: '1 1 45%', pl: '1rem', display: "flex", alignSelf: "center" }}> 
                                <RadioButtonAutoForm
                                    key={key}
                                    name={`${props.section}[${key}]`}
                                    label={convertJSONKeyToTitle(key)}
                                    disabled={props.disabled}
                                    labelMinWidth={`${maxLabelWidth * 8}px`} // Approximate character width
                                    values={props.formFields[key].values}
                                    defaultValue={formSectionInputs.current[key]}
                                    required={props.formFields[key].required}
                                />
                            </Box>
                        );
                    }
                    else if (props.formFields[key]["type"] === 'boolean') {
                        if (props.formFields[key]["display"] === 'checkbox') {
                            return (
                                <Box sx={{ flex: '1 1 45%', pl: '1rem' }}>
                                    <CheckBoxAutoForm
                                        key={key}
                                        name={`${props.section}[${key}]`}
                                        label={convertJSONKeyToTitle(key)}
                                        disabled={props.disabled}
                                        labelMinWidth={`${maxLabelWidth * 8}px`} // Approximate character width
                                        defaultValue={formSectionInputs.current[key]}
                                        required={props.formFields[key].required}
                                    />
                                </Box>
                            )
                        }
                    }
                    else if (props.formFields[key]["type"] === 'list') {
                        if (props.formFields[key]["display"] === "multi_select_dropdown") {
                            if (!Array.isArray(formSectionInputs.current[key])) {
                                let fetchEmAll = [];
                                for (let option in props.formFields[key].values) {
                                    if (formSectionInputs.current[key].includes(props.formFields[key].values[option])) {
                                        fetchEmAll.push(props.formFields[key].values[option]);
                                    }
                                }
                                formSectionInputs.current[key] = fetchEmAll;
                            }
                            return (
                                <Box sx={{ flex: '1 1 45%', pl: '1rem' }}> 
                                    <SelectInputAutoForm 
                                        key={key}
                                        name={`${props.section}[${key}]`}
                                        label={convertJSONKeyToTitle(key)}
                                        disabled={props.disabled}
                                        labelMinWidth={`${maxLabelWidth * 8}px`} // Approximate character width
                                        options={props.formFields[key].values}
                                        defaultValue={formSectionInputs.current[key]}
                                        required={props.formFields[key].required}
                                        multiple={true}
                                    />
                                </Box>
                            );
                        }
                        else if (props.formFields[key]["display"] === "image") {
                            return (
                            <Box sx={{ flex: '45%', px: '1%'}}>
                                <LocationOfInjuries 
                                key={key}
                                name={`${props.section}[${key}]`}
                                label={convertJSONKeyToTitle(key)} 
                                initialValue={formSectionInputs.current[key]} 
                                required={props.formFields[key].required}
                                disabled={props.disabled}
                                />
                            </Box>)
                        } else if (props.formFields[key]["display"] === "table" && !("headers" in props.formFields[key])) {
                            return (
                                <Box sx={{ flex: '100%', pl: '1rem' }}>
                                    <VitalsTableInput
                                        key={key}
                                        name={`${props.section}[${key}]`}
                                        label={convertJSONKeyToTitle(key)}
                                        disabled={props.disabled}
                                        labelMinWidth={`${maxLabelWidth * 8}px`} // Approximate character width
                                        required={props.formFields[key].required}
                                        defaultValue={formSectionInputs.current[key]}
                                    />
                                </Box>
                            );
                        } else if (props.formFields[key]["display"] === "table" && ("headers" in props.formFields[key])) {
                            return (
                                <Box sx={{ flex: '100%', pl: '1rem' }}>
                                    <HeadersTableInput
                                        key={key}
                                        name={`${props.section}[${key}]`}
                                        label={convertJSONKeyToTitle(key)}
                                        disabled={props.disabled}
                                        headers={props.formFields[key].headers}
                                        orientation={false}
                                        required={props.formFields[key].required}
                                        defaultValue={formSectionInputs.current[key]}
                                    />
                                </Box>
                            );
                        }
                    }  else if (props.formFields[key]["type"] === 'dictionary') {
                        if (props.formFields[key]["display"] === "table" && "headers" in props.formFields[key] && props.formFields[key]["orientation"] === "horizontal") {
                            return (
                                <Box sx={{ flex: '100%', pl: '1rem' }}>
                                    <HeadersTableInput
                                        key={key}
                                        name={`${props.section}[${key}]`}
                                        label={convertJSONKeyToTitle(key)}
                                        disabled={props.disabled}
                                        headers={props.formFields[key].headers}
                                        orientation={true}
                                        required={props.formFields[key].required}
                                        defaultValue={formSectionInputs.current[key]}
                                    />
                                </Box>
                            );
                        }
                    }
                })}
        </Box>
}

export default AutoBuildFormSection;

